import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Layout, BarChart3, ShoppingCart, FileText, Zap, Monitor, Smartphone, Tablet, Copy, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StandaloneVibeCoder = () => {
  const navigate = useNavigate();
  const [showCategories, setShowCategories] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chatMessages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [activeTab, setActiveTab] = useState('Preview');
  const [deviceMode, setDeviceMode] = useState('desktop');
  const [selectedFile, setSelectedFile] = useState('App.jsx');
  const [previewContent, setPreviewContent] = useState('');
  const [copied, setCopied] = useState(false);

  const categories = [
    {
      id: 'landing',
      name: 'Landing Page',
      icon: <Layout className="w-8 h-8" />,
      color: '#38bdf8',
      description: 'Beautiful marketing pages that convert visitors'
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: <BarChart3 className="w-8 h-8" />,
      color: '#a78bfa',
      description: 'Data visualization and analytics interfaces'
    },
    {
      id: 'ecommerce',
      name: 'E-commerce',
      icon: <ShoppingCart className="w-8 h-8" />,
      color: '#4ade80',
      description: 'Product pages, carts, and checkout flows'
    },
    {
      id: 'saas',
      name: 'SaaS App',
      icon: <Zap className="w-8 h-8" />,
      color: '#ff4500',
      description: 'Full-featured web applications'
    },
    {
      id: 'portfolio',
      name: 'Portfolio',
      icon: <FileText className="w-8 h-8" />,
      color: '#fbbf24',
      description: 'Personal or professional portfolio sites'
    },
    {
      id: 'custom',
      name: 'Custom App',
      icon: <Sparkles className="w-8 h-8" />,
      color: '#f472b6',
      description: 'Describe any app you want to build'
    }
  ];

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategories(false);
    
    // Add initial system message
    const systemMessage = {
      role: 'system',
      content: `Welcome to Nova Vibe Coder! You've selected: **${category.name}**\n\n${category.description}\n\nDescribe what you want to build, and I'll generate complete React code for you.`,
      timestamp: new Date().toISOString()
    };
    setMessages([systemMessage]);
  };

  const handleStartFromScratch = () => {
    setShowCategories(false);
    const systemMessage = {
      role: 'system',
      content: 'Welcome to Nova Vibe Coder! Describe any app you want to build, and I\'ll generate complete React code for you.',
      timestamp: new Date().toISOString()
    };
    setMessages([systemMessage]);
  };

  const handleSendMessage = async (message) => {
    if (!message.trim() || isGenerating) return;

    // Add user message
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsGenerating(true);

    try {
      // Call real Nova API
      const response = await axios.post(`${BACKEND_URL}/api/nova/generate`, {
        prompt: message,
        history: chatMessages,
        app_type: selectedCategory?.name
      });

      const novaMessage = {
        role: 'nova',
        content: response.data.explanation,
        files: response.data.files,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, novaMessage]);
      setGeneratedFiles(response.data.files);
      setHasGenerated(true);

      // Generate preview from the main App.jsx file
      const appFile = response.data.files.find(f => f.name === 'App.jsx' || f.name.includes('App'));
      if (appFile) {
        setPreviewContent(generatePreviewHTML(appFile.content, response.data.files));
      }

    } catch (error) {
      console.error('Error calling Nova:', error);
      const errorMessage = {
        role: 'nova',
        content: `Error generating code: ${error.response?.data?.detail || error.message}. Please try again.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePreviewHTML = (appCode, allFiles) => {
    // Combine all component files into a single HTML preview
    const componentsCode = allFiles
      .filter(f => f.name.includes('components/'))
      .map(f => {
        const componentName = f.name.split('/').pop().replace('.jsx', '');
        return `const ${componentName} = () => {
          ${f.content.replace(/^import.*$/gm, '').replace(/^export default.*$/gm, '')}
        };`;
      })
      .join('\n\n');

    const mainAppCode = appCode
      .replace(/^import.*$/gm, '')
      .replace(/^export default.*$/gm, '')
      .replace(/function App\(\)/, 'const App = () =>')
      .replace('export default App;', '');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Generated App Preview</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>
    body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
    * { box-sizing: border-box; }
  </style>
</head>
<body>
  <div id="root"></div>
  
  <script type="text/babel">
    const { useState, useEffect, useRef } = React;
    
    // Component definitions
    ${componentsCode}
    
    // Main App component
    ${mainAppCode}
    const App = () => {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white overflow-x-hidden">
          ${mainAppCode.includes('Header') ? '<Header />' : ''}
          <main>
            ${mainAppCode.includes('Hero') ? '<Hero />' : ''}
            ${mainAppCode.includes('Features') ? '<Features />' : ''}
            ${mainAppCode.includes('Benefits') ? '<Benefits />' : ''}
            ${mainAppCode.includes('HowItWorks') ? '<HowItWorks />' : ''}
            ${mainAppCode.includes('CTA') ? '<CTA />' : ''}
          </main>
          ${mainAppCode.includes('Footer') ? '<Footer />' : ''}
        </div>
      );
    };
    
    // Render
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(<App />);
  </script>
</body>
</html>`;
  };

  if (showCategories) {
    return (
      <div className="min-h-screen bg-[#09090e] text-[#f0eff6] flex flex-col">
        {/* Header */}
        <div className="border-b border-[#22222e] px-8 py-6">
          <button 
            onClick={() => navigate('/')}
            className="text-[#7b7a88] hover:text-white transition-colors mb-4"
            style={{ fontFamily: 'DM Mono, monospace' }}
          >
            ← Back to Home
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-4xl">✨</span>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Nova Vibe Coder
            </h1>
          </div>
          <p className="text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
            Powered by Amazon Nova Lite • Generate complete React apps from natural language
          </p>
        </div>

        {/* Category Selection */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            What do you want to build?
          </h2>
          <p className="text-[#7b7a88] mb-12 text-center max-w-2xl" style={{ fontFamily: 'DM Mono, monospace' }}>
            Choose a category to get started, or describe your custom app
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mb-8">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category)}
                className="bg-[#17171c] border border-[#22222e] rounded-xl p-8 hover:border-[#ff4500] transition-all duration-300 text-left group"
                data-testid={`category-${category.id}`}
              >
                <div 
                  className="mb-4 inline-block p-3 rounded-lg"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <div style={{ color: category.color }}>
                    {category.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#ff4500] transition-colors" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {category.name}
                </h3>
                <p className="text-[#7b7a88] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>
                  {category.description}
                </p>
              </button>
            ))}
          </div>

          <button
            onClick={handleStartFromScratch}
            className="px-8 py-3 border-2 border-[#7b7a88] text-[#7b7a88] rounded-lg hover:border-white hover:text-white transition-colors"
            style={{ fontFamily: 'DM Mono, monospace' }}
            data-testid="start-from-scratch-btn"
          >
            Or start with a blank canvas
          </button>
        </div>
      </div>
    );
  }

  // Main coding interface with full tabs
  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderCodeView = () => {
    if (!generatedFiles || generatedFiles.length === 0) return null;

    const file = generatedFiles.find(f => f.name === selectedFile) || generatedFiles[0];
    
    return (
      <div className="flex-1 bg-[#17171c] flex flex-col">
        {/* File Tab Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#22222e]">
          <div className="flex items-center gap-2">
            <span className="text-[#38bdf8]">📄</span>
            <span className="text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>{file.name}</span>
          </div>
          <button 
            onClick={() => handleCopy(file.content)}
            className="p-1.5 text-[#7b7a88] hover:text-white transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-[#4ade80]" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        {/* Code Display */}
        <div className="flex-1 overflow-auto p-4 text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
          <pre className="text-[#f0eff6] leading-relaxed">
            {file.content.split('\n').map((line, i) => (
              <div key={i} className="hover:bg-[#1e1e26]">
                <span className="inline-block text-[#3a3a48] w-12 text-right mr-4 select-none">{i + 1}</span>
                <span>{line}</span>
              </div>
            ))}
          </pre>
        </div>
      </div>
    );
  };

  const renderFilesView = () => {
    if (!generatedFiles || generatedFiles.length === 0) return null;

    return (
      <div className="flex-1 bg-[#17171c] p-4">
        <h3 className="text-xs text-[#7b7a88] mb-3 font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>
          GENERATED FILES
        </h3>
        <div className="space-y-1">
          {generatedFiles.map((file, idx) => (
            <div
              key={idx}
              onClick={() => {
                setSelectedFile(file.name);
                setActiveTab('Code');
              }}
              className="flex items-center gap-2 px-3 py-2 rounded hover:bg-[#1e1e26] cursor-pointer"
              style={{ fontFamily: 'DM Mono, monospace' }}
            >
              <span className="text-[#38bdf8]">📄</span>
              <span className="text-sm text-white">{file.name}</span>
              <span className="text-xs text-[#ff4500] ml-auto">●</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPreviewView = () => {
    if (!hasGenerated) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse">✨</div>
            <h3 className="text-2xl text-[#7b7a88] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Describe your app to Nova
            </h3>
            <p className="text-sm text-[#3a3a48]" style={{ fontFamily: 'DM Mono, monospace' }}>
              Your generated app will appear here
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col">
        {/* Device Switcher */}
        <div className="border-b border-[#22222e] px-6 py-3 flex items-center gap-4">
          <button
            onClick={() => setDeviceMode('desktop')}
            className={`p-2 rounded transition-colors ${
              deviceMode === 'desktop' ? 'text-[#ff4500] bg-[#ff4500]/10' : 'text-[#7b7a88] hover:text-white'
            }`}
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeviceMode('tablet')}
            className={`p-2 rounded transition-colors ${
              deviceMode === 'tablet' ? 'text-[#ff4500] bg-[#ff4500]/10' : 'text-[#7b7a88] hover:text-white'
            }`}
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeviceMode('mobile')}
            className={`p-2 rounded transition-colors ${
              deviceMode === 'mobile' ? 'text-[#ff4500] bg-[#ff4500]/10' : 'text-[#7b7a88] hover:text-white'
            }`}
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <div className="ml-auto text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
            Live Preview • {generatedFiles.length} files
          </div>
        </div>

        {/* Preview Content - Live Iframe */}
        <div className="flex-1 flex items-start justify-center p-8 overflow-auto bg-[#09090e]">
          {previewContent ? (
            <div 
              className="bg-white shadow-2xl transition-all duration-500"
              style={{
                width: deviceMode === 'desktop' ? '100%' : deviceMode === 'tablet' ? '768px' : '390px',
                maxWidth: '100%',
                minHeight: '800px',
                borderRadius: deviceMode !== 'desktop' ? '12px' : '0'
              }}
            >
              <iframe
                srcDoc={previewContent}
                className="w-full h-full"
                style={{
                  border: 'none',
                  minHeight: '800px',
                  borderRadius: deviceMode !== 'desktop' ? '12px' : '0'
                }}
                title="App Preview"
                sandbox="allow-scripts"
              />
            </div>
          ) : (
            <div className="bg-white rounded-xl p-8 max-w-2xl">
              <div className="text-center">
                <div className="text-5xl mb-4">⚠️</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Preview Error</h3>
                <p className="text-gray-600 mb-4">
                  Unable to generate preview. View the code in the Code tab.
                </p>
                <button
                  onClick={() => setActiveTab('Code')}
                  className="px-6 py-2 bg-[#ff4500] text-white rounded-lg hover:bg-[#e03d00] transition-colors"
                >
                  View Code →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-[#09090e] text-[#f0eff6] flex flex-col overflow-hidden">
      {/* Topbar */}
      <div className="border-b border-[#22222e] px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setShowCategories(true)}
              className="text-[#7b7a88] hover:text-white transition-colors"
              style={{ fontFamily: 'DM Mono, monospace' }}
            >
              ← Back
            </button>
            <span className="text-[#ff4500]">⬤</span>
            <span className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Nova Vibe Coder</span>
            {selectedCategory && (
              <>
                <span className="text-[#7b7a88]">›</span>
                <span style={{ color: selectedCategory.color }}>{selectedCategory.name}</span>
              </>
            )}
          </div>

          {/* Tab Switcher */}
          {hasGenerated && (
            <div className="flex items-center gap-1 bg-[#17171c] rounded-lg p-1">
              {['Preview', 'Code', 'Files'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-1.5 rounded transition-colors ${
                    activeTab === tab 
                      ? 'bg-[#ff4500] text-white' 
                      : 'text-[#7b7a88] hover:text-white'
                  }`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                  data-testid={`tab-${tab.toLowerCase()}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#4ade80] rounded-full animate-pulse"></div>
            <span className="text-[#4ade80] text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>Nova connected</span>
          </div>
        </div>
      </div>

      {/* 3-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat Panel */}
        <div className="w-96 border-r border-[#22222e] flex flex-col bg-[#17171c]">
          <div className="p-4 border-b border-[#22222e]">
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Chat with Nova</h3>
            <p className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
              Describe what you want to build
            </p>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={msg.role === 'user' ? 'ml-8' : 'mr-8'}>
                {msg.role === 'system' && (
                  <div className="bg-[#1e1e26] border-l-4 border-[#ff4500] rounded p-3">
                    <p className="text-xs text-[#7b7a88] whitespace-pre-line" style={{ fontFamily: 'DM Mono, monospace' }}>
                      {msg.content}
                    </p>
                  </div>
                )}
                {msg.role === 'user' && (
                  <div className="bg-[#ff4500]/20 rounded-lg p-3">
                    <p className="text-sm" style={{ fontFamily: 'DM Mono, monospace' }}>{msg.content}</p>
                  </div>
                )}
                {msg.role === 'nova' && (
                  <div className="bg-[#1e1e26] rounded-lg p-3">
                    <p className="text-sm mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>{msg.content}</p>
                    {msg.files && msg.files.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#22222e]">
                        <p className="text-xs text-[#7b7a88] mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                          Generated {msg.files.length} files
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {isGenerating && (
              <div className="mr-8 bg-[#1e1e26] rounded-lg p-3">
                <p className="text-sm text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                  Nova is generating code<span className="animate-pulse">...</span>
                </p>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[#22222e]">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  handleSendMessage(inputValue);
                }
              }}
              placeholder="Describe your app..."
              className="w-full bg-[#09090e] border border-[#22222e] rounded-lg p-3 text-sm resize-none focus:outline-none focus:border-[#ff4500]"
              style={{ fontFamily: 'DM Mono, monospace' }}
              rows={3}
              disabled={isGenerating}
            />
            <button
              onClick={() => handleSendMessage(inputValue)}
              disabled={!inputValue.trim() || isGenerating}
              className="mt-2 w-full px-4 py-2 bg-[#ff4500] text-white rounded-lg hover:bg-[#e03d00] transition-colors disabled:opacity-50"
              style={{ fontFamily: 'DM Mono, monospace' }}
            >
              Generate Code →
            </button>
          </div>
        </div>

        {/* Main Content Panel */}
        <div className="flex-1 bg-[#09090e]">
          {activeTab === 'Preview' && renderPreviewView()}
          {activeTab === 'Code' && renderCodeView()}
          {activeTab === 'Files' && renderFilesView()}
        </div>
      </div>
    </div>
  );
};

export default StandaloneVibeCoder;
